import { OnboardingGoals } from "@/features/onboarding/components/onboarding-questions";

/**
 * Which story the launch bar tells. `enabled` workspaces already have
 * multimodal analysis switched on by an operator and need to find the control,
 * not book a call; `prospect` workspaces cannot see the feature at all and
 * booking is the only way to get it.
 */
export type LaunchBarSegment = "enabled" | "prospect";

export interface LaunchBarLine {
  key: string;
  text: string;
}

export interface LaunchBarCopy {
  /** Always rendered first, and the only line shown under reduced motion. */
  primary: LaunchBarLine;
  /** Rotated after the primary line. May be empty. */
  fragments: LaunchBarLine[];
}

/**
 * A prompt the user could paste into chat today, one per modality, chosen for
 * their stated goal. A concrete prompt against their own recordings does more
 * to explain the capability than another sentence describing it. `transcript`
 * covers what is available with no analysis mode turned on at all — every
 * account has it, so it doubles as the chat suggestion for accounts without
 * multimodal access.
 */
export interface GoalPrompts {
  transcript: LaunchBarLine;
  audio: LaunchBarLine;
  video: LaunchBarLine;
}

const GOAL_PROMPTS: Record<string, GoalPrompts> = {
  [OnboardingGoals.SALES]: {
    transcript: { key: "LAUNCH_BAR.PROMPT.SALES.TRANSCRIPT", text: "Try: \"what objections came up on this call?\"" },
    audio: { key: "LAUNCH_BAR.PROMPT.SALES.AUDIO", text: "Try: \"how did their tone change after pricing came up?\"" },
    video: { key: "LAUNCH_BAR.PROMPT.SALES.VIDEO", text: "Try: \"what was on screen when they pushed back?\"" },
  },
  [OnboardingGoals.RESEARCH]: {
    transcript: { key: "LAUNCH_BAR.PROMPT.RESEARCH.TRANSCRIPT", text: "Try: \"what themes came up most across sessions?\"" },
    audio: { key: "LAUNCH_BAR.PROMPT.RESEARCH.AUDIO", text: "Try: \"where did this participant hesitate?\"" },
    video: { key: "LAUNCH_BAR.PROMPT.RESEARCH.VIDEO", text: "Try: \"what did their face do when I asked about price?\"" },
  },
  [OnboardingGoals.MEETINGS]: {
    transcript: { key: "LAUNCH_BAR.PROMPT.MEETINGS.TRANSCRIPT", text: "Try: \"what decisions or action items were agreed on?\"" },
    audio: { key: "LAUNCH_BAR.PROMPT.MEETINGS.AUDIO", text: "Try: \"where did energy drop in this meeting?\"" },
    video: { key: "LAUNCH_BAR.PROMPT.MEETINGS.VIDEO", text: "Try: \"what was on the shared screen when we decided?\"" },
  },
  [OnboardingGoals.MEETING_TRANSCRIPTION]: {
    transcript: { key: "LAUNCH_BAR.PROMPT.MEETING.TRANSCRIPT", text: "Try: \"what decisions or action items were agreed on?\"" },
    audio: { key: "LAUNCH_BAR.PROMPT.MEETING.AUDIO", text: "Try: \"where did energy drop in this meeting?\"" },
    video: { key: "LAUNCH_BAR.PROMPT.MEETING.VIDEO", text: "Try: \"what was on the shared screen when we decided?\"" },
  },
  [OnboardingGoals.INTERVIEW]: {
    transcript: { key: "LAUNCH_BAR.PROMPT.INTERVIEW.TRANSCRIPT", text: "Try: \"what themes came up most across interviews?\"" },
    audio: { key: "LAUNCH_BAR.PROMPT.INTERVIEW.AUDIO", text: "Try: \"where did this participant hesitate?\"" },
    video: { key: "LAUNCH_BAR.PROMPT.INTERVIEW.VIDEO", text: "Try: \"what did their face do when I asked about price?\"" },
  },
  [OnboardingGoals.PHONE_CALL]: {
    transcript: { key: "LAUNCH_BAR.PROMPT.PHONE_CALL.TRANSCRIPT", text: "Try: \"summarize the next steps agreed at end of call.\"" },
    audio: { key: "LAUNCH_BAR.PROMPT.PHONE_CALL.AUDIO", text: "Try: \"how did their tone change after pricing came up?\"" },
    video: { key: "LAUNCH_BAR.PROMPT.PHONE_CALL.VIDEO", text: "Try: \"what was on screen when they pushed back?\"" },
  },
  [OnboardingGoals.SURVEY_RESPONSE]: {
    transcript: { key: "LAUNCH_BAR.PROMPT.SURVEY.TRANSCRIPT", text: "Try: \"which responses mention the same complaint?\"" },
    audio: { key: "LAUNCH_BAR.PROMPT.SURVEY.AUDIO", text: "Try: \"which responses sound frustrated, not neutral?\"" },
    video: { key: "LAUNCH_BAR.PROMPT.SURVEY.VIDEO", text: "Try: \"what did respondents show on camera that the text missed?\"" },
  },
  [OnboardingGoals.EMBED_RECORDER]: {
    transcript: { key: "LAUNCH_BAR.PROMPT.RECORDER.TRANSCRIPT", text: "Try: \"summarize the most common answer to question one.\"" },
    audio: { key: "LAUNCH_BAR.PROMPT.RECORDER.AUDIO", text: "Try: \"which submissions sound most enthusiastic?\"" },
    video: { key: "LAUNCH_BAR.PROMPT.RECORDER.VIDEO", text: "Try: \"how did respondents look answering question three?\"" },
  },
  [OnboardingGoals.TRANSLATE_MEDIA]: {
    transcript: { key: "LAUNCH_BAR.PROMPT.TRANSLATE.TRANSCRIPT", text: "Try: \"translate this transcript and flag anything unclear.\"" },
    audio: { key: "LAUNCH_BAR.PROMPT.TRANSLATE.AUDIO", text: "Try: \"does the translation carry the same emotion?\"" },
    video: { key: "LAUNCH_BAR.PROMPT.TRANSLATE.VIDEO", text: "Try: \"what on-screen text still needs translating?\"" },
  },
  [OnboardingGoals.APIS]: {
    transcript: { key: "LAUNCH_BAR.PROMPT.APIS.TRANSCRIPT", text: "Try: \"return the transcript as structured fields.\"" },
    audio: { key: "LAUNCH_BAR.PROMPT.APIS.AUDIO", text: "Try: \"return tone and energy per speaker as fields.\"" },
    video: { key: "LAUNCH_BAR.PROMPT.APIS.VIDEO", text: "Try: \"extract on-screen text and expressions per timestamp.\"" },
  },
};

const GOAL_PROMPTS_DEFAULT: GoalPrompts = {
  transcript: { key: "LAUNCH_BAR.PROMPT.DEFAULT.TRANSCRIPT", text: "Try: \"summarize this recording in a few sentences.\"" },
  audio: { key: "LAUNCH_BAR.PROMPT.DEFAULT.AUDIO", text: "Try: \"what does their tone say that the words do not?\"" },
  video: { key: "LAUNCH_BAR.PROMPT.DEFAULT.VIDEO", text: "Try: \"what was on screen that the transcript missed?\"" },
};

/** The prompt pair for a goal, falling back to the general pair. */
export function goalPrompts(goal?: string): GoalPrompts {
  // hasOwn guards prototype-chain keys ("constructor", "__proto__") resolving to truthy non-prompt objects
  return (goal && Object.hasOwn(GOAL_PROMPTS, goal) && GOAL_PROMPTS[goal]) || GOAL_PROMPTS_DEFAULT;
}

/**
 * Body copy for the locked multimodal upsell popover (the chat analysis-mode
 * dropdown), personalised by the same onboarding goal that picks the launch
 * bar's sample prompts. One short pitch sentence plus the same call ask, so the
 * popover speaks to what the user actually records. Unknown or missing goals
 * fall back to the generic body.
 */
const LOCKED_UPSELL_BODIES: Record<string, LaunchBarLine> = {
  [OnboardingGoals.SALES]: {
    key: "MULTIMODAL_PROMO.CHAT_LOCKED.BODY_SALES",
    text: "Hear how their tone shifted at decision moments like pricing. Book a call and we will turn it on for your workspace.",
  },
  [OnboardingGoals.RESEARCH]: {
    key: "MULTIMODAL_PROMO.CHAT_LOCKED.BODY_RESEARCH",
    text: "Catch where participants hesitated and what their expressions showed. Book a call and we will turn it on for your workspace.",
  },
  [OnboardingGoals.MEETINGS]: {
    key: "MULTIMODAL_PROMO.CHAT_LOCKED.BODY_MEETINGS",
    text: "Hear where energy dropped and see what was on screen when your team decided. Book a call and we will turn it on for your workspace.",
  },
  [OnboardingGoals.MEETING_TRANSCRIPTION]: {
    key: "MULTIMODAL_PROMO.CHAT_LOCKED.BODY_MEETING",
    text: "Hear where energy dropped and see what was on screen when your team decided. Book a call and we will turn it on for your workspace.",
  },
  [OnboardingGoals.INTERVIEW]: {
    key: "MULTIMODAL_PROMO.CHAT_LOCKED.BODY_INTERVIEW",
    text: "Catch where participants hesitated and what their expressions showed. Book a call and we will turn it on for your workspace.",
  },
  [OnboardingGoals.PHONE_CALL]: {
    key: "MULTIMODAL_PROMO.CHAT_LOCKED.BODY_PHONE_CALL",
    text: "Hear how their tone shifted at decision moments like pricing. Book a call and we will turn it on for your workspace.",
  },
  [OnboardingGoals.SURVEY_RESPONSE]: {
    key: "MULTIMODAL_PROMO.CHAT_LOCKED.BODY_SURVEY",
    text: "Hear which responses sound frustrated and see what respondents showed on camera. Book a call and we will turn it on for your workspace.",
  },
  [OnboardingGoals.EMBED_RECORDER]: {
    key: "MULTIMODAL_PROMO.CHAT_LOCKED.BODY_RECORDER",
    text: "Hear which submissions sound most enthusiastic and see how respondents answered on camera. Book a call and we will turn it on for your workspace.",
  },
  [OnboardingGoals.TRANSLATE_MEDIA]: {
    key: "MULTIMODAL_PROMO.CHAT_LOCKED.BODY_TRANSLATE",
    text: "Check whether the translation carries the same emotion and what on-screen text was missed. Book a call and we will turn it on for your workspace.",
  },
  [OnboardingGoals.APIS]: {
    key: "MULTIMODAL_PROMO.CHAT_LOCKED.BODY_APIS",
    text: "Extract tone, energy, and on-screen content as structured fields. Book a call and we will turn it on for your workspace.",
  },
};

const LOCKED_UPSELL_BODY_DEFAULT: LaunchBarLine = {
  key: "MULTIMODAL_PROMO.CHAT_LOCKED.BODY",
  text: "Analyze tone, emotion, body language, and what was on screen. Book a call and we will turn it on for your workspace.",
};

/**
 * The goal-specific locked-upsell body, or null when the goal is unknown —
 * for surfaces that carry their own generic fallback copy.
 */
export function goalLockedUpsellBody(goal?: string): LaunchBarLine | null {
  return (goal && LOCKED_UPSELL_BODIES[goal]) || null;
}

/** The locked-upsell popover body for a goal, falling back to the generic pitch. */
export function lockedUpsellBody(goal?: string): LaunchBarLine {
  return goalLockedUpsellBody(goal) ?? LOCKED_UPSELL_BODY_DEFAULT;
}

const AUDIO_CAPABILITY: LaunchBarLine = {
  key: "LAUNCH_BAR.AUDIO",
  text: "Audio analysis: analyze tone, emotion, energy and more.",
};

const VISUAL_CAPABILITY: LaunchBarLine = {
  key: "LAUNCH_BAR.VISUALS",
  text: "Visuals: extract body language, screen sharing content, facial expressions and more.",
};

const DEEPER: LaunchBarLine = {
  key: "LAUNCH_BAR.DEEPER",
  text: "Get deeper insights than transcript-only analysis.",
};

const ASK_PROSPECT: LaunchBarLine = {
  key: "LAUNCH_BAR.ASK",
  text: "Book a call to get first access and expert setup.",
};

const ASK_CUSTOMER: LaunchBarLine = {
  key: "LAUNCH_BAR.ASK_CUSTOMER",
  text: "Talk to your account team to turn it on for your workspace.",
};

/**
 * The enabled segment's ask is support, not sales. They already have the feature and
 * paid for it; the risk is that they never find the control or never get past the
 * first prompt, not that they fail to buy.
 */
const ASK_SUPPORT: LaunchBarLine = {
  key: "LAUNCH_BAR.ASK_SUPPORT",
  text: "Want help getting the most out of it? Book time with our team.",
};

export interface LaunchBarCopyInput {
  segment: LaunchBarSegment;
  /** `user.onboarding.goal` — a literal OnboardingGoals value. */
  goal?: string;
  /**
   * Active paid account. The ask points at their account team rather than a
   * consult, matching how useBookingCta routes the calendar.
   */
  isPaidActive: boolean;
  /**
   * Append the access-and-setup ask. Kill switch rather than an experiment arm:
   * it rides on top of both copy arms so turning it off does not invalidate the
   * arm read.
   */
  showIncentive?: boolean;
}

/**
 * Resolve the launch bar's message set for one user.
 */
export function launchBarCopy({
  segment,
  goal,
  isPaidActive,
  showIncentive = false,
}: LaunchBarCopyInput): LaunchBarCopy {
  const prompts = goalPrompts(goal);

  if (segment === "enabled") {
    return {
      primary: {
        key: "LAUNCH_BAR.ENABLED.PRIMARY",
        text: "Audio and video analysis is now live in your account.",
      },
      fragments: [
        { key: "LAUNCH_BAR.ENABLED.WHERE", text: "Open any recording and turn on analysis in chat." },
        prompts.audio,
        prompts.video,
        DEEPER,
        ASK_SUPPORT,
      ],
    };
  }

  const ask = isPaidActive ? ASK_CUSTOMER : ASK_PROSPECT;

  return {
    primary: {
      key: "LAUNCH_BAR.CAPABILITY.PRIMARY",
      text: "You can now analyze tone and visuals in Speak AI.",
    },
    fragments: [
      AUDIO_CAPABILITY,
      VISUAL_CAPABILITY,
      prompts.audio,
      prompts.video,
      DEEPER,
      ...(showIncentive ? [ask] : []),
    ],
  };
}
